// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 Ben Richardson — https://benrichardson.dev
// See ADDITIONAL-TERMS.md for the section 7(b) attribution requirement.
//
// Waft — the deterministic catch simulation. Fireflies bloom in zones on the seeded
// SCHEDULE; the player feeds a per-zone motion energy (from the camera, a tap, or keys);
// crossing SWAT_THRESH in a zone (subject to a per-zone COOLDOWN so one wave = one catch)
// resolves whatever is there — catch a firefly (score + combo), get stung by a wasp
// (penalty + combo reset), or whiff on an empty zone. A firefly left to fade is a miss
// (combo reset). NO Math.random and a FIXED substep, so (mode, seed, energy sequence) is
// byte-identical everywhere — which makes the same-seed race fair by construction and
// the balance sim a fair referee.
//
// At most one mote is alive per zone (a schedule invariant), so `byZone[z]` is the single
// thing a swat in zone z resolves.

import type { Mode } from './modes';
import { zonesOf } from './modes';
import { buildSchedule, flyCount, type Mote } from './schedule';

/* ── tunables (one place; the balance sim refereed these) ─────────────────── */
export const STEP_MS = 1000 / 60; // fixed sim substep
export const SWAT_THRESH = 0.12; // per-zone motion energy that counts as a wave
export const COOLDOWN_MS = 240; // per-zone refractory: one sustained wave → one catch
export const POINTS_BASE = 10; // points for a catch at zero combo
export const POINTS_PER_COMBO = 2; // extra points per combo step (capped)
export const COMBO_CAP = 25; // combo beyond this no longer raises the multiplier
export const STING_PENALTY = 40; // points lost for waving over a wasp

/** Points a catch is worth at a given combo (the combo BEFORE this catch). */
export function catchPoints(combo: number): number {
  return POINTS_BASE + POINTS_PER_COMBO * Math.min(combo, COMBO_CAP);
}

export type MoteState = 'pending' | 'alive' | 'caught' | 'missed' | 'stung' | 'gone';

export interface LiveMote extends Mote {
  state: MoteState;
}

export type GameEvent =
  | { t: 'spawn'; id: number; zone: number; kind: 'fly' | 'wasp' }
  | { t: 'catch'; id: number; zone: number; combo: number; pts: number }
  | { t: 'miss'; id: number; zone: number }
  | { t: 'sting'; id: number; zone: number }
  | { t: 'whiff'; zone: number }
  | { t: 'over' };

export interface GameState {
  mode: Mode;
  seed: number;
  zones: number;
  schedule: LiveMote[];
  /** The alive mote in each zone, or null. */
  byZone: (LiveMote | null)[];
  /** Index of the next un-spawned mote in `schedule`. */
  next: number;
  cooldown: number[];
  clock: number;
  score: number;
  combo: number;
  bestCombo: number;
  catches: number;
  misses: number;
  stings: number;
  whiffs: number;
  /** Total catchable fireflies in the schedule — the knowable "perfect". */
  flies: number;
  over: boolean;
  events: GameEvent[];
  acc: number;
}

export function createGame(mode: Mode, seed: number): GameState {
  const schedule = buildSchedule(mode, seed).map((m) => ({ ...m, state: 'pending' as MoteState }));
  const zones = zonesOf(mode);
  return {
    mode,
    seed,
    zones,
    schedule,
    byZone: new Array(zones).fill(null),
    next: 0,
    cooldown: new Array(zones).fill(0),
    clock: 0,
    score: 0,
    combo: 0,
    bestCombo: 0,
    catches: 0,
    misses: 0,
    stings: 0,
    whiffs: 0,
    flies: flyCount(schedule),
    over: false,
    events: [],
    acc: 0,
  };
}

/** Drain accumulated events (for sound/particles or the audit), clearing the buffer. */
export function drainEvents(s: GameState): GameEvent[] {
  if (s.events.length === 0) return [];
  const out = s.events;
  s.events = [];
  return out;
}

/** One fixed substep: expire faders, spawn new motes, then resolve swats. */
function substep(s: GameState, energy: readonly number[]): void {
  if (s.over) return;
  s.clock += STEP_MS;

  // 1) Expire any alive mote whose glow has ended.
  for (let z = 0; z < s.zones; z++) {
    const m = s.byZone[z];
    if (m && m.state === 'alive' && s.clock >= m.tExpire) {
      if (m.kind === 'fly') {
        m.state = 'missed';
        s.misses++;
        s.combo = 0;
        s.events.push({ t: 'miss', id: m.id, zone: z });
      } else {
        m.state = 'gone'; // a wasp that fades on its own is harmless
      }
      s.byZone[z] = null;
    }
  }

  // 2) Spawn any motes due now (schedule guarantees the zone is clear).
  while (s.next < s.schedule.length && s.schedule[s.next].tSpawn <= s.clock) {
    const m = s.schedule[s.next++];
    m.state = 'alive';
    s.byZone[m.zone] = m;
    s.events.push({ t: 'spawn', id: m.id, zone: m.zone, kind: m.kind });
  }

  // 3) Resolve swats.
  for (let z = 0; z < s.zones; z++) {
    if (s.cooldown[z] > 0) s.cooldown[z] = Math.max(0, s.cooldown[z] - STEP_MS);
    const e = energy[z] ?? 0;
    if (e < SWAT_THRESH || s.cooldown[z] > 0) continue;
    s.cooldown[z] = COOLDOWN_MS;
    const m = s.byZone[z];
    if (!m || m.state !== 'alive') {
      // A wave over an empty square breaks the combo — precision is the skill, and it is
      // what stops random mashing from scoring as well as careful play (measured: without
      // this, a blind masher ties a perfect reader — the mechanic was not load-bearing).
      s.whiffs++;
      s.combo = 0;
      s.events.push({ t: 'whiff', zone: z });
      continue;
    }
    if (m.kind === 'fly') {
      const pts = catchPoints(s.combo);
      s.events.push({ t: 'catch', id: m.id, zone: z, combo: s.combo, pts });
      s.score += pts;
      s.combo++;
      if (s.combo > s.bestCombo) s.bestCombo = s.combo;
      s.catches++;
      m.state = 'caught';
    } else {
      s.score = Math.max(0, s.score - STING_PENALTY);
      s.combo = 0;
      s.stings++;
      m.state = 'stung';
      s.events.push({ t: 'sting', id: m.id, zone: z });
    }
    s.byZone[z] = null;
  }

  // 4) End of round.
  if (s.clock >= s.mode.limitMs) {
    s.over = true;
    s.events.push({ t: 'over' });
  }
}

/** Advance by `dtMs` of wall-clock in fixed substeps, holding `energy` across them. */
export function step(s: GameState, energy: readonly number[], dtMs: number): void {
  if (s.over) return;
  s.acc += Math.min(dtMs, 100); // clamp a huge tab-refocus jump
  let guard = 0;
  while (s.acc >= STEP_MS && !s.over && guard < 1000) {
    substep(s, energy);
    s.acc -= STEP_MS;
    guard++;
  }
}

/** Fraction of the round elapsed, 0..1. */
export function progress(s: GameState): number {
  return Math.max(0, Math.min(1, s.clock / s.mode.limitMs));
}
